import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAffAuth } from "../store";
import { useT } from "../i18n";

function fmtUsd(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

export default function AffPayouts() {
  const { partner, refresh } = useAffAuth();
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate/payouts");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const k = partner?.kpis || {};
  const threshold = partner?.min_payout_usd ?? 25;
  const balance = k.pending_usd ?? 0;
  const canRequest = balance >= threshold && !k.requested_payout;

  const submitRequest = async () => {
    setRequesting(true);
    try {
      await api.post("/affiliate/payouts/request", { amount_usd: balance });
      toast.success(t("payouts.requested"));
      await refresh();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="aff-payouts-page">
      <header>
        <h1 className="text-2xl font-medium tracking-tight">{t("payouts.title")}</h1>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
          {t("payouts.subtitle", { threshold: threshold.toFixed(0) })}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-emerald-500/40 bg-secondary/10 px-4 py-4 col-span-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            {t("payouts.available")}
          </div>
          <div className="text-3xl font-semibold mt-1 tabular-nums" data-testid="aff-balance">
            {fmtUsd(balance)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t("payouts.threshold")} <strong>${threshold.toFixed(0)}</strong>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <button
            disabled={!canRequest || requesting}
            onClick={submitRequest}
            data-testid="aff-request-payout-btn"
            className={`w-full px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              canRequest && !requesting
                ? "bg-emerald-600 text-white hover:bg-emerald-500"
                : "bg-secondary/50 text-muted-foreground cursor-not-allowed"
            }`}
          >
            <Wallet className="w-4 h-4" />
            {k.requested_payout
              ? t("payouts.cta.pending")
              : balance < threshold
              ? t("payouts.cta.below")
              : requesting
              ? t("payouts.cta.busy")
              : t("payouts.cta")}
          </button>
        </div>
      </div>

      <div className="border border-border" data-testid="aff-payouts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-3 py-2">{t("payouts.col.date")}</th>
              <th className="text-right px-3 py-2">{t("payouts.col.usd")}</th>
              <th className="text-right px-3 py-2">{t("payouts.col.native")}</th>
              <th className="text-left px-3 py-2">{t("payouts.col.method")}</th>
              <th className="text-left px-3 py-2">{t("payouts.col.status")}</th>
              <th className="text-left px-3 py-2">{t("payouts.col.note")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 py-8 text-center text-muted-foreground text-xs">
                  {t("payouts.empty")}
                </td>
              </tr>
            )}
            {items.map((p) => (
              <tr
                key={p.id}
                className="border-b border-border"
                data-testid={`aff-payout-${p.id}`}
              >
                <td className="px-3 py-2 font-mono text-xs">
                  {(p.paid_at || p.created_at).slice(0, 16).replace("T", " ")}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(p.amount_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {Number(p.amount_native).toFixed(2)} {p.currency}
                </td>
                <td className="px-3 py-2">{p.method}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
                      p.status === "paid"
                        ? "border-emerald-500/40 text-emerald-400"
                        : "border-amber-500/40 text-amber-400"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{p.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
